import { useCallback, useMemo, useState } from 'react';

import { isFeatureEnabled } from '../config/featureFlags';

const DEFAULT_CRITERIA = Object.freeze({
  broadcaster: null,
  category: null,
  tags: [],
});

const BROADCASTER_FEATURE_ENABLED = isFeatureEnabled('broadcasterDiscovery');
const CATEGORY_FEATURE_ENABLED = isFeatureEnabled('categoryDiscovery');
const DISCOVERY_TAGS_ENABLED = isFeatureEnabled('discoveryTags');

const slugify = (value) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const sanitizeLogin = (value) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_');

const sanitizeSlug = (value) => {
  if (typeof value !== 'string') {
    return '';
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }
  return slugify(trimmed);
};

const sanitizeBroadcaster = (value) => {
  if (!value) {
    return null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    const login = sanitizeLogin(trimmed);
    const displayName = trimmed;
    return login
      ? {
          id: null,
          login,
          displayName,
        }
      : null;
  }

  if (typeof value !== 'object') {
    return null;
  }

  const id = typeof value.id === 'string' && value.id.trim() ? value.id.trim() : null;
  const loginCandidate =
    typeof value.login === 'string' && value.login.trim() ? value.login.trim() : null;
  const displayCandidate =
    typeof value.displayName === 'string' && value.displayName.trim()
      ? value.displayName.trim()
      : typeof value.name === 'string' && value.name.trim()
        ? value.name.trim()
        : loginCandidate;

  const login = loginCandidate ? sanitizeLogin(loginCandidate) : null;
  const fallbackLogin = !login && displayCandidate ? sanitizeLogin(displayCandidate) : null;

  if (!login && !fallbackLogin && !id) {
    return null;
  }

  return {
    id,
    login: login || fallbackLogin || null,
    displayName: displayCandidate || loginCandidate || id || null,
  };
};

const sanitizeCategory = (value) => {
  if (!value) {
    return null;
  }

  if (typeof value !== 'object') {
    const name = String(value).trim();
    if (!name) {
      return null;
    }
    return {
      id: null,
      name,
      slug: sanitizeSlug(name),
    };
  }

  const id = typeof value.id === 'string' && value.id.trim() ? value.id.trim() : null;
  const nameCandidate =
    typeof value.name === 'string' && value.name.trim()
      ? value.name.trim()
      : typeof value.label === 'string' && value.label.trim()
        ? value.label.trim()
        : null;
  const slugCandidate =
    typeof value.slug === 'string' && value.slug.trim() ? sanitizeSlug(value.slug) : null;
  const slug = slugCandidate || (nameCandidate ? sanitizeSlug(nameCandidate) : null);

  if (!slug && !id) {
    return null;
  }

  return {
    id,
    name: nameCandidate || slug || id,
    slug,
  };
};

const sanitizeTag = (tag) => {
  if (!tag) {
    return null;
  }

  if (typeof tag === 'string') {
    const trimmed = tag.trim();
    if (!trimmed) {
      return null;
    }
    const slug = sanitizeSlug(trimmed);
    if (!slug) {
      return null;
    }
    return { id: slug, label: trimmed };
  }

  if (typeof tag !== 'object') {
    return null;
  }

  const idCandidate =
    typeof tag.id === 'string' && tag.id.trim() ? sanitizeSlug(tag.id) : null;
  const labelCandidate =
    typeof tag.label === 'string' && tag.label.trim()
      ? tag.label.trim()
      : typeof tag.name === 'string' && tag.name.trim()
        ? tag.name.trim()
        : idCandidate;
  const slug = idCandidate || (labelCandidate ? sanitizeSlug(labelCandidate) : null);

  if (!slug) {
    return null;
  }

  return {
    id: slug,
    label: labelCandidate || slug,
  };
};

const sanitizeTags = (tags) => {
  if (!Array.isArray(tags) || !tags.length) {
    return [];
  }

  const seen = new Set();
  const normalized = [];

  tags.forEach((tag) => {
    const sanitized = sanitizeTag(tag);
    if (!sanitized || !sanitized.id) {
      return;
    }
    if (seen.has(sanitized.id)) {
      return;
    }
    seen.add(sanitized.id);
    normalized.push(sanitized);
  });

  return normalized;
};

const areBroadcastersEqual = (left, right) => {
  if (left === right) {
    return true;
  }
  if (!left || !right) {
    return false;
  }
  if (left.id && right.id) {
    return left.id === right.id;
  }
  return left.login === right.login;
};

const areCategoriesEqual = (left, right) => {
  if (left === right) {
    return true;
  }
  if (!left || !right) {
    return false;
  }
  if (left.id && right.id) {
    return left.id === right.id;
  }
  return left.slug === right.slug;
};

const areTagsEqual = (left, right) => {
  if (left === right) {
    return true;
  }
  if (!Array.isArray(left) || !Array.isArray(right)) {
    return false;
  }
  if (left.length !== right.length) {
    return false;
  }
  return left.every((tag, index) => tag.id === right[index].id);
};

const parseTagList = (value) => {
  if (typeof value !== 'string') {
    return [];
  }

  return value
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
};

export const serializeDiscoveryCriteria = (criteria) => {
  const params = new URLSearchParams();
  if (!criteria) {
    return params;
  }

  const broadcaster = BROADCASTER_FEATURE_ENABLED
    ? sanitizeBroadcaster(criteria.broadcaster)
    : null;
  const category = CATEGORY_FEATURE_ENABLED
    ? sanitizeCategory(criteria.category)
    : null;
  const tags = DISCOVERY_TAGS_ENABLED ? sanitizeTags(criteria.tags) : [];

  if (broadcaster?.login) {
    params.set('broadcaster', broadcaster.login);
  } else if (broadcaster?.id) {
    params.set('broadcaster', broadcaster.id);
  }

  if (category?.slug) {
    params.set('category', category.slug);
  } else if (category?.id) {
    params.set('category', category.id);
  }

  if (DISCOVERY_TAGS_ENABLED && tags.length) {
    const tagList = tags
      .map((tag) => tag.id)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
    if (tagList.length) {
      params.set('tags', tagList.join(','));
    }
  }

  return params;
};

export const deriveDiscoveryCriteriaFromSearch = (search) => {
  if (typeof search !== 'string') {
    return null;
  }

  const trimmed = search.trim();
  if (!trimmed) {
    return null;
  }

  let params;
  try {
    params = new URLSearchParams(trimmed.startsWith('?') ? trimmed.slice(1) : trimmed);
  } catch (error) {
    return null;
  }

  let hasCriteria = false;
  const nextCriteria = {
    broadcaster: null,
    category: null,
    tags: [],
  };

  if (BROADCASTER_FEATURE_ENABLED) {
    const broadcasterCandidate = params.get('broadcaster');
    const sanitizedBroadcaster = sanitizeBroadcaster(broadcasterCandidate);
    if (sanitizedBroadcaster) {
      nextCriteria.broadcaster = sanitizedBroadcaster;
      hasCriteria = true;
    }
  }

  if (CATEGORY_FEATURE_ENABLED) {
    const categoryCandidate = params.get('category');
    const sanitizedCategory = sanitizeCategory(categoryCandidate);
    if (sanitizedCategory) {
      nextCriteria.category = sanitizedCategory;
      hasCriteria = true;
    }
  }

  if (DISCOVERY_TAGS_ENABLED) {
    const tagsCandidate = params.get('tags');
    const sanitizedTags = sanitizeTags(parseTagList(tagsCandidate));
    if (sanitizedTags.length) {
      nextCriteria.tags = sanitizedTags;
      hasCriteria = true;
    }
  }

  return hasCriteria ? nextCriteria : null;
};

export function useDiscoveryCriteria(initialCriteria = DEFAULT_CRITERIA) {
  const [criteria, setCriteria] = useState(() => ({
    broadcaster:
      BROADCASTER_FEATURE_ENABLED
        ? sanitizeBroadcaster(initialCriteria?.broadcaster) ?? null
        : null,
    category:
      CATEGORY_FEATURE_ENABLED
        ? sanitizeCategory(initialCriteria?.category) ?? null
        : null,
    tags:
      DISCOVERY_TAGS_ENABLED
        ? sanitizeTags(initialCriteria?.tags) ?? []
        : [],
  }));

  const criteriaSignature = useMemo(
    () => serializeDiscoveryCriteria(criteria).toString(),
    [criteria],
  );

  const setBroadcaster = useCallback((nextBroadcaster) => {
    if (!BROADCASTER_FEATURE_ENABLED) {
      return;
    }
    const normalized = sanitizeBroadcaster(nextBroadcaster);
    setCriteria((prev) => {
      if (areBroadcastersEqual(prev.broadcaster, normalized)) {
        return prev;
      }
      if (normalized) {
        return { ...prev, broadcaster: normalized, category: null };
      }
      return { ...prev, broadcaster: normalized };
    });
  }, []);

  const setCategory = useCallback((nextCategory) => {
    if (!CATEGORY_FEATURE_ENABLED) {
      return;
    }
    const normalized = sanitizeCategory(nextCategory);
    setCriteria((prev) => {
      if (areCategoriesEqual(prev.category, normalized)) {
        return prev;
      }
      if (normalized) {
        return { ...prev, category: normalized, broadcaster: null };
      }
      return { ...prev, category: normalized };
    });
  }, []);

  const setTags = useCallback((nextTags) => {
    if (!DISCOVERY_TAGS_ENABLED) {
      return;
    }
    const normalized = sanitizeTags(nextTags);
    setCriteria((prev) => {
      if (areTagsEqual(prev.tags, normalized)) {
        return prev;
      }
      return { ...prev, tags: normalized };
    });
  }, []);

  const toggleTag = useCallback((tag) => {
    if (!DISCOVERY_TAGS_ENABLED) {
      return;
    }
    const normalized = sanitizeTag(tag);
    if (!normalized) {
      return;
    }
    setCriteria((prev) => {
      const exists = prev.tags.some((item) => item.id === normalized.id);
      if (exists) {
        const nextTags = prev.tags.filter((item) => item.id !== normalized.id);
        return { ...prev, tags: nextTags };
      }
      return { ...prev, tags: [...prev.tags, normalized] };
    });
  }, []);

  const clearCriteria = useCallback(() => {
    setCriteria({ broadcaster: null, category: null, tags: [] });
  }, []);

  const serializeCriteria = useCallback(() => serializeDiscoveryCriteria(criteria), [criteria]);

  return {
    criteria,
    setBroadcaster,
    setCategory,
    setTags,
    toggleTag,
    clearCriteria,
    serializeCriteria,
    criteriaSignature,
  };
}
